import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { adminApi, api, getAdminUser, clearAdminSession, setAdminSession, LOGOS } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Search, UserCheck, CheckCircle2, Ticket, ScanLine, X, RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      timeZone: "Asia/Jakarta", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    }) + " WIB";
  } catch { return ""; }
};

function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { username, password });
      setAdminSession(data.user);
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login gagal");
    }
    setLoading(false);
  };
  return (
    <div className="min-h-screen bg-[#2F703E] flex flex-col items-center justify-center px-6">
      <img src={LOGOS.kbi} alt="KBI" className="h-14 mb-6 bg-white/95 rounded-lg p-2" />
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-7 shadow-xl">
        <div className="h-12 w-12 rounded-full bg-[#2F703E]/10 flex items-center justify-center mb-3">
          <ScanLine className="h-6 w-6 text-[#2F703E]" />
        </div>
        <h1 className="font-serif-display text-2xl text-[#2F703E]">Check-in Peserta</h1>
        <p className="text-sm text-[#7A6A5E] mb-5">Masuk dengan akun Anda untuk mencatat kehadiran peserta.</p>
        <Label htmlFor="uname">Username</Label>
        <Input id="uname" value={username} onChange={(e) => setUsername(e.target.value)}
          className="mt-1.5 mb-3" data-testid="checkin-login-username" placeholder="username" autoCapitalize="none" />
        <Label htmlFor="pw">Password</Label>
        <Input id="pw" type="password" inputMode="text" value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5" data-testid="checkin-login-password" placeholder="••••••••" />
        <Button type="submit" disabled={loading} data-testid="checkin-login-btn"
          className="w-full mt-5 bg-[#B26A1E] hover:bg-[#8A3A12] rounded-full h-11">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Masuk
        </Button>
      </form>
    </div>
  );
}

export default function CheckinPage() {
  const [authed, setAuthed] = useState(!!getAdminUser());
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [popup, setPopup] = useState(null);
  const [allSessions, setAllSessions] = useState([]);

  useEffect(() => {
    api.get("/event").then(({ data }) => setAllSessions(data.sessions || [])).catch(() => {});
  }, []);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/participants");
      setParticipants(data);
    } catch (err) {
      if (err?.response?.status === 401) { clearAdminSession(); setAuthed(false); }
      else if (!silent) toast.error("Gagal memuat data");
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);
  // Refresh stats near real-time (silent, tanpa mengganggu pencarian)
  useEffect(() => {
    if (!authed) return;
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [authed, load]);

  const doCheckin = async (o) => {
    setBusyId(o.id);
    try {
      const { data } = await adminApi.post(`/admin/orders/${o.id}/checkin`);
      setPopup({ ...data, channel: o.channel, session: o.session });
      await load();
    } catch (err) { toast.error("Gagal check-in"); }
    setBusyId(null);
  };

  const doUncheckin = async (o) => {
    if (!window.confirm(`Batalkan check-in untuk ${o.name}? Peserta akan ditandai BELUM hadir kembali.`)) return;
    setBusyId(o.id);
    try {
      await adminApi.post(`/admin/orders/${o.id}/uncheckin`);
      toast.success(`Check-in ${o.name} dibatalkan`);
      await load();
    } catch (err) { toast.error("Gagal membatalkan check-in"); }
    setBusyId(null);
  };

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const q = query.trim().toLowerCase();
  const nq = q.replace(/[\s-]/g, "");
  // Cari-dulu: tidak menampilkan seluruh daftar (berat). Hasil muncul saat mengetik ≥ 2 karakter.
  const results = q.length < 2 ? [] : participants.filter(
    (o) =>
      o.name.toLowerCase().includes(q) ||
      o.phone.replace(/[\s-]/g, "").includes(nq) ||
      String(o.order_no || "").includes(nq)
  ).slice(0, 15);
  const totalHadir = participants.filter((o) => o.checked_in).length;
  const tix = (o) => o.qty || o.seats?.length || 0;
  const totalTix = participants.reduce((s, o) => s + tix(o), 0);
  const hadirTix = participants.filter((o) => o.checked_in).reduce((s, o) => s + tix(o), 0);
  const sessionStats = Object.values(
    participants.reduce((acc, o) => {
      const key = o.session?.id ?? "none";
      if (!acc[key]) acc[key] = { id: key, name: o.session?.name || "Tanpa Sesi", time: o.session?.time || "", totalTix: 0, hadirTix: 0 };
      acc[key].totalTix += tix(o);
      if (o.checked_in) acc[key].hadirTix += tix(o);
      return acc;
    }, allSessions.reduce((seed, s) => {
      seed[s.id] = { id: s.id, name: s.name, time: s.time, totalTix: 0, hadirTix: 0 };
      return seed;
    }, {}))
  ).sort((a, b) => String(a.name).localeCompare(String(b.name), "id", { numeric: true }));

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-[#2F703E] text-white shadow-md">
        <div className="px-4 py-4 max-w-6xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGOS.kbi} alt="KBI" className="h-11 bg-white/95 rounded-lg p-1" />
            <div>
              <p className="text-lg font-bold leading-tight">Check-in Peserta</p>
              <p className="text-xs text-white/85 leading-tight">Film Dokumenter · MBI Kepri</p>
            </div>
          </div>
          <button onClick={() => { adminApi.post("/admin/logout").catch(() => {}); clearAdminSession(); setAuthed(false); }}
            data-testid="checkin-logout" className="text-sm font-semibold text-white bg-white/15 hover:bg-white/25 rounded-full px-4 py-2 transition-colors">Keluar</button>
        </div>
        <div className="px-4 pb-4 max-w-6xl mx-auto w-full">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#7A6A5E]" />
            <Input data-testid="checkin-search-mobile" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama, nomor HP, atau no. order..." className="pl-12 pr-11 h-14 text-lg bg-white text-[#2C1E16] rounded-2xl shadow-lg" />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7A6A5E]">
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="mt-3 space-y-2.5" data-testid="checkin-stats">
            <div className="flex items-center justify-between rounded-2xl bg-white/12 border border-white/20 px-4 py-3">
              <span className="text-sm font-semibold text-white/90">Total tamu hadir</span>
              <span className="flex items-baseline gap-2">
                <b className="font-serif-display text-3xl text-[#E4C57E] leading-none" data-testid="checkin-stat-total">{hadirTix}</b>
                <span className="text-white/80 text-lg">/ {totalTix}</span>
                <span className="text-white/60 text-xs ml-1">({totalHadir}/{participants.length} pesanan)</span>
              </span>
            </div>
            {sessionStats.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="checkin-session-stats">
                {sessionStats.map((s) => {
                  const done = s.totalTix > 0 && s.hadirTix >= s.totalTix;
                  return (
                    <span key={s.id} data-testid={`checkin-stat-session-${s.id}`}
                      className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm text-white transition-colors",
                        done ? "bg-[#2F703E] border-[#E4C57E] ring-2 ring-[#E4C57E]/60" : "bg-white/12 border-white/20")}>
                      <span className="font-bold">{s.name}</span>
                      <span className={cn("rounded-full px-2 py-0.5 font-bold text-sm", done ? "bg-[#E4C57E] text-[#5B4636]" : "bg-white text-[#2F703E]")}>{s.hadirTix}/{s.totalTix}</span>
                      {done && <span className="inline-flex items-center gap-0.5 font-bold text-[#E4C57E] text-xs"><CheckCircle2 className="h-3.5 w-3.5" />PENUH</span>}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 py-5 max-w-6xl mx-auto pb-24">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
        ) : q.length < 2 ? (
          <div className="text-center py-16" data-testid="checkin-prompt">
            <Search className="h-12 w-12 text-[#B26A1E]/40 mx-auto mb-4" />
            <p className="text-base text-[#7A6A5E]">Ketik <b>nomor order</b>, <b>nama</b>, atau <b>no. HP</b> untuk mencari peserta.</p>
          </div>
        ) : results.length === 0 ? (
          <p className="text-center text-sm text-[#7A6A5E] py-16">Tidak ada peserta yang cocok dengan "{query}".</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {results.map((o) => (
            <motion.div key={o.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`checkin-card-${o.id.slice(0, 8)}`}
              className={cn("rounded-2xl border bg-white p-5 shadow-sm",
                o.checked_in ? "border-[#2F703E]/40 bg-[#2F703E]/5" : "border-border")}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-xl text-[#2C1E16] break-words">{o.name}</p>
                    {o.order_no ? (
                      <span className="shrink-0 text-xs font-bold text-[#7A241F] bg-[#7A241F]/10 px-2 py-0.5 rounded-md">
                        #{o.order_no}
                      </span>
                    ) : null}
                    {o.channel === "vip" && <span className="shrink-0 text-[11px] font-bold text-[#B26A1E] bg-[#B26A1E]/15 px-2 py-0.5 rounded-md">VIP</span>}
                    {o.channel === "manual" && <span className="shrink-0 text-[11px] font-bold text-[#8A3A12] bg-[#B26A1E]/10 px-2 py-0.5 rounded-md">MANUAL</span>}
                  </div>
                  <p className="text-sm text-[#7A6A5E] mt-0.5">{o.phone}</p>
                  <p className="text-sm font-medium text-[#8A3A12] mt-1">{o.session?.name} · {o.session?.time} · {o.qty} tiket</p>
                </div>
                {o.checked_in ? (
                  <span className="shrink-0 inline-flex items-center gap-1 text-sm text-[#255E33] font-bold bg-[#2F703E]/15 px-3 py-1.5 rounded-full">
                    <CheckCircle2 className="h-4 w-4" /> Hadir
                  </span>
                ) : null}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {o.seats.map((s) => (
                  <span key={s} className="px-4 py-2 rounded-xl bg-[#B26A1E]/10 text-[#8A3A12] text-lg font-bold">{s}</span>
                ))}
              </div>

              {o.checked_in ? (
                <div className="mt-4 flex items-center justify-between gap-2">
                  <p className="text-xs text-[#7A6A5E]">
                    Check-in: {fmtTime(o.checked_in_at)}
                    {o.checked_in_by ? <> · oleh <b className="text-[#255E33]">{o.checked_in_by}</b></> : null}
                  </p>
                  <Button onClick={() => doUncheckin(o)} disabled={busyId === o.id} variant="outline"
                    data-testid={`checkin-undo-btn-${o.id.slice(0, 8)}`}
                    className="shrink-0 h-9 px-3 text-xs border-[#7A241F]/40 text-[#7A241F] hover:bg-[#7A241F]/10">
                    {busyId === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
                    Batalkan Check-in
                  </Button>
                </div>
              ) : (
                <Button onClick={() => doCheckin(o)} disabled={busyId === o.id}
                  data-testid={`checkin-mobile-btn-${o.id.slice(0, 8)}`}
                  className="w-full mt-4 h-14 bg-[#B26A1E] hover:bg-[#8A3A12] text-white font-bold text-lg rounded-2xl shadow-md">
                  {busyId === o.id ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <UserCheck className="h-5 w-5 mr-2" />}
                  Tandai Sudah Datang
                </Button>
              )}
            </motion.div>
          ))}
          </div>
        )}
      </div>

      {/* Reminder popup */}
      <Dialog open={!!popup} onOpenChange={() => setPopup(null)}>
        <DialogContent data-testid="checkin-mobile-popup" className="max-w-sm rounded-2xl">
          <DialogHeader>
            <div className="h-11 w-11 rounded-full bg-[#2F703E]/15 flex items-center justify-center mb-2">
              <Ticket className="h-5 w-5 text-[#2F703E]" />
            </div>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Peserta Sudah Datang</DialogTitle>
            <DialogDescription className="sr-only">Detail tiket dan kursi peserta yang sudah check-in</DialogDescription>
          </DialogHeader>
          {popup && (
            <div className="text-sm space-y-3">
              <p><b>{popup.name}</b> — {popup.session?.name} · {popup.session?.time}</p>
              <p className="text-xs text-[#2F703E] font-medium">✓ Check-in: {fmtTime(popup.checked_in_at || new Date().toISOString())}</p>
              {popup.channel === "manual" ? (
                <div className="rounded-lg bg-[#B26A1E]/10 p-4">
                  <p className="text-[#8A3A12] font-medium mb-2">Serahkan tiket (Order Manual):</p>
                  <p className="font-serif-display text-2xl text-[#7A241F] mb-2">{popup.session?.name?.toUpperCase()} · {popup.session?.time}</p>
                  <p className="text-xs text-[#7A6A5E] mb-1">Nomor kursi <b className="text-[#8A3A12]">({popup.seats?.length || popup.qty} tiket)</b>:</p>
                  <div className="flex flex-wrap gap-2">
                    {(popup.seats || []).map((s) => (
                      <span key={s} className="px-3 py-1.5 rounded-md bg-white text-[#8A3A12] font-bold border border-[#B26A1E]/30">{s}</span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg bg-[#B26A1E]/10 p-4">
                  <p className="text-[#8A3A12] font-medium mb-2">Serahkan tiket:</p>
                  <p className="font-serif-display text-2xl text-[#7A241F] mb-2" data-testid="checkin-mobile-session">{popup.session?.name?.toUpperCase()} · {popup.session?.time}</p>
                  <p className="text-xs text-[#7A6A5E] mb-1">Nomor kursi <b className="text-[#8A3A12]">({popup.seats.length} tiket)</b>:</p>
                  <div className="flex flex-wrap gap-2">
                    {popup.seats.map((s) => (
                      <span key={s} className="px-3 py-1.5 rounded-md bg-white text-[#8A3A12] font-bold border border-[#B26A1E]/30">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPopup(null)} className="bg-[#7A241F] hover:bg-[#5E1B17] w-full h-11" data-testid="checkin-mobile-ok">
              Sudah Saya Berikan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
